/****************************************************
 * Module Name:  memoDetails.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Container, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import { v4 as uuidv4 } from "uuid";
import StoneDetailModal from "../common/stoneDetailModal";
import "./../../style/modalstyle.css";
import CommonSnackbar from "./commonSnackBar";
import logo from '../../images/pansuriya.jpg'

const MemoDetails = ({ memoId, handleClose, show }) => {
    const [data, setData] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [stoneId, setStoneId] = useState(0);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);

    useEffect(() => {
        handleSearch(memoId);
    }, []);

    const handleSearch = (memoId) => {
        setDetailLoader(true);
        ReportProxy.GetSingleMemoDetail(
            memoId,
            (response) => {
                if (response) {
                    let data = response
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = uuidv4();
                    }
                    setData(data);
                    setDetailLoader(false);
                } else {
                    setDetailLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };
    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const columnName = [
        {
            field: "memoid",
            headerName: "MEMO ID",
            headerAlign: "center",
            align: "center",
            width: 80,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "status",
            headerName: "MEMO STATUS",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            field: "memodetailstatus",
            headerName: "STONE STATUS",
            flex: 1,
            minWidth: 150,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "user",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.userid + " | " + params.row.username,
        },
        {
            field: "customer",
            headerName: "CUSTOMER USERNAME",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.customerid + " | " + params.row.customerusername,
        },
        {
            field: "customerfirstname",
            headerName: "CUSTOMER FIRSTNAME",
            headerAlign: "center",
            align: "center",
            minWidth: 170,
            flex: 1,
        },
        {
            field: "customerlastname",
            headerName: "CUSTOMER LASTNAME",
            headerAlign: "center",
            align: "center",
            minWidth: 170,
            flex: 1,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => params.row.locationid + " | " + params.row.location,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "givenat",
            headerName: "GIVEN",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "updatedat",
            headerName: "UPDATED",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "lastupdatedat",
            headerName: "LAST UPDATED",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "expectedreceive",
            headerName: "EXPECTED RECEIVE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "memodetailremark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 150,
            renderCell: renderCellExpand,
        },
    ];

    const rows = data;
    const columns = columnName;
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            <Modal show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                {showStoneDetailModal ? (
                    <StoneDetailModal
                        show={showStoneDetailModal}
                        stoneId={stoneId}
                        handleClose={() => setShowStoneDetailModal(false)}
                    />
                ) : null}
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Memo Detail
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box
                        style={{
                            textAlign: "center",
                            fontSize: "xx-large",
                            marginTop: "0.5rem",
                            width: "100%",
                        }}
                    >
                        Memo Detail
                    </Box>
                    <Container maxWidth="xxl">
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                height: "50vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "8px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "15px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "22px",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                        width: "0.4em",
                                        height: "0.7em",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                        background: "#f1f1f1",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#888",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                        background: "#555",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row.id}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            // locationid: false,
                                            // id: false,
                                        },
                                    },
                                    sorting: {
                                        sortModel: [{ field: "updatedat", sort: "desc" }],
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {detailLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(memoId)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer", mr: 1 }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MemoDetails;
